import Backend from 'backend';
import {ShoppingCart} from './shopping-cart';
import {inject, observable, customElement, bindable, bindingMode} from 'aurelia-framework';
import {Router} from 'aurelia-router';
import {SpecialPriceCalculator} from "./specialPriceCalculator";
import {RMetaPixel} from "../../util/r-meta-pixel";
import {RGTag} from "../../util/r-gtag";

@customElement('customizable-content-component')
@inject(Element, Backend, ShoppingCart, Router, SpecialPriceCalculator)
export class CustomizableContentComponent {
  element;
  backend;
  shoppingCart;
  router;
  specialPriceCalculator;

  @observable({changeHandler: 'valueOfGoodsChanged'})
  valueOfGoods;

  @observable({changeHandler: 'chosenProductsChanged'})
  chosenProducts = [];

  calculatedPrice;
  initValues = {};

  @bindable() screenMode = true;
  @bindable() showFixedBar = true;
  @bindable({defaultBindingMode: bindingMode.twoWay}) selectedProductCategorySeoKey;

  constructor(element, backend, shoppingCart, router, specialPriceCalculator) {
    this.element = element;
    this.router = router;
    this.backend = backend;
    this.shoppingCart = shoppingCart;
    this.specialPriceCalculator = specialPriceCalculator;
  }

  attached() {
    return this.backend.CmsFragmentPublicQueryHandler_handle({items: [
          {cmsFragmentKey: "checkout_flow__customizable_content__section1"}
        ]})
      .then(result => {
        this.cmsFragments = result;
      });
  }

  // Switched from attached() to bind() after having issues with loading initValues after going back and forth between checkout steps.
  bind() {
    // Get shopping cart info
    let info = this.shoppingCart.getInfo();
    this.crateName = info.crateProduct.name;

    // Put each in the init values
    this.initValues = {};
    info.getAllSubProducts().forEach(cartSubProduct => {
      this.initValues[cartSubProduct.productId] =
        {
          quantity: cartSubProduct.quantity,
          everyTime: !cartSubProduct.oneTime
        };
    })
  }

  valueOfGoodsChanged() {
    // Min price
    this.calculatedPrice = this.specialPriceCalculator.calculateSumIncVat(this.shoppingCart.getInfo().crateProduct.productId, this.valueOfGoods);
    this.diffToMinPrice = this.specialPriceCalculator.calculateMinPriceDiffIncVat(this.shoppingCart.getInfo().crateProduct.productId, this.valueOfGoods);
  }

  next() {
    // Store in shopping cart
    this.shoppingCart.clearWeekExtraAndCustomizationProducts();
    this.chosenProducts
      // The SubProductComponent allows quantity zero if initValues was non zero because it should be possible to remove items,
      // but that is not necessary here since we are working with client-side data and we just cleared the product list.
      .filter(product => product.quantity > 0)
      .forEach(product => this.shoppingCart.addCustomizationProduct(product.productId, product.quantity, false));

    // Meta Pixel: Track checkout
    this.shoppingCart.trackInitiateCheckout();

    // Signal that this step is complete.
    this.element.dispatchEvent(new CustomEvent('checkout-flow-next', {
      detail: {},
      bubbles: true
    }));
  }

  back() {
    this.element.dispatchEvent(new CustomEvent('checkout-flow-back', {
      detail: {},
      bubbles: true
    }));
  }

  chosenProductsChanged() {
    this.addChosenProductsToCart();
  }

  quantityChanged(event) {
    // Track in Meta Pixel only if quantity increased.
    if (event.detail.quantityIncrement > 0) {
      RMetaPixel.trackAddToCart(
        event.detail.productArticleNumber,
        event.detail.priceIncVat);
      RGTag.trackAddToCart(
        event.detail.productArticleNumber,
        event.detail.productName,
        event.detail.priceIncVat);
    }
  }

  addChosenProductsToCart() {
    // Store in shopping cart
    let self = this;
    this.shoppingCart.atomic(cart => {
      cart.clearWeekExtraAndCustomizationProducts();
      self.chosenProducts
        // The SubProductComponent allows quantity zero if initValues was non zero because it should be possible to remove items,
        // but that is not necessary here since we are working with client-side data and we just cleared the product list.
        .filter(product => product.quantity > 0)
        .forEach(product => {
          // If everytime is set use that, if not default to everytime=true i.e. onetime=false because that is what
          // makes sense for customizable content.
          let oneTime = product.everyTime === undefined
            ? false
            : !product.everyTime;
          if (product.isBundle) {
            // This is a hack because bundles inside crates would require sub-sub-lines.
            // See comment on same behavior in ChangeCustomizableContentCommandHandler.java
            cart.addWeekExtraProducts(product.productId, product.quantity, oneTime)
          } else {
            // This is normal. Add sub product inside customizable crate.
            cart.addCustomizationProduct(product.productId, product.quantity, oneTime);
          }
        });
      });
  }
}
